import { useState } from "react";
import { Info, Link2, ChevronDown, ChevronRight } from "lucide-react";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { MODULE_LABELS } from "../lib/moduleRoutes.js";
import { TRIAGE_ACTIONS, issueStatusLabel } from "../lib/issueStatus.js";
import { AsyncButton } from "./ui/async-button.js";
import { SelectField } from "./ui/select-field.js";
import { Textarea } from "./ui/textarea.js";
import { Input } from "./ui/input.js";
import { DateInput } from "./ui/date-input.js";
import { Label } from "./ui/label.js";
import { Badge } from "./ui/badge.js";

export interface IssueAssignee { userId: string; name: string; email: string }

export interface TriageIssue {
  id: string;
  status: string;
  priority: string;
  module: string | null;
  assignee: string | null;
  assigneeName: string | null;
  statusReason?: string | null;
  deferredUntil?: string | null;
  duplicateOf?: { id: string; displayId: number; title: string } | null;
  duplicates?: { id: string; displayId: number; title: string }[];
  pageUrl?: string | null;
  browser?: string | null;
  os?: string | null;
  viewport?: string | null;
  appVersion?: string | null;
  userAgent?: string | null;
  consoleErrors?: string[] | null;
}

const fmtId = (n: number) => `HRMS-${String(n).padStart(4, "0")}`;

function errorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { error?: string } } }).response?.data;
  return data?.error ?? (err instanceof Error ? err.message : "Something went wrong");
}

/** Assignee, priority, module and triage status controls for engineers in the issue drawer. */
export function IssueTriagePanel({ issue, assignees, onChanged, onOpenIssue }: {
  issue: TriageIssue;
  assignees: IssueAssignee[];
  onChanged: () => Promise<void>;
  onOpenIssue: (id: string) => void;
}) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [duplicateOf, setDuplicateOf] = useState("");
  const [deferredUntil, setDeferredUntil] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const action = TRIAGE_ACTIONS.find(a => a.status === target);
  const choices = TRIAGE_ACTIONS.filter(a => a.status !== issue.status);
  const closedForGood = issue.status === "closed";

  async function patch(body: Record<string, unknown>, source: string) {
    setErr(null);
    try {
      await api.patch(`/issues/${issue.id}`, body);
      await onChanged();
    } catch (e) {
      logError(`IssueTriagePanel.${source}`, e);
      setErr(errorMessage(e));
    }
  }

  async function assign(userId: string) {
    setErr(null);
    try {
      await api.post(`/issues/${issue.id}/assign`, { assignee: userId || null });
      await onChanged();
    } catch (e) {
      logError("IssueTriagePanel.assign", e);
      setErr(errorMessage(e));
    }
  }

  async function applyStatus() {
    setErr(null);
    if (!action) { setErr("Pick a status"); throw new Error("validation"); }
    if (action.needsReason && !reason.trim()) { setErr("Give a reason"); throw new Error("validation"); }
    if (target === "duplicate" && !duplicateOf.trim()) { setErr("Enter the original issue, e.g. HRMS-0042"); throw new Error("validation"); }
    try {
      await api.post(`/issues/${issue.id}/status`, {
        status: target,
        reason: reason.trim() || null,
        duplicateOf: target === "duplicate" ? duplicateOf.trim() : null,
        deferredUntil: target === "deferred" && deferredUntil ? deferredUntil : null,
      });
      setTarget(""); setReason(""); setDuplicateOf(""); setDeferredUntil("");
      await onChanged();
    } catch (e) {
      logError("IssueTriagePanel.applyStatus", e);
      setErr(errorMessage(e));
      throw e;
    }
  }

  // An assignee who left the participant list still shows as the current value.
  const assigneeOptions = issue.assignee && !assignees.some(a => a.userId === issue.assignee)
    ? [...assignees, { userId: issue.assignee, name: issue.assigneeName ?? "Current assignee", email: "" }]
    : assignees;

  return (
    <div className="flex flex-col gap-3 border-b border-surface-200 bg-surface-50 px-5 py-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <Label>Assignee</Label>
          <SelectField size="sm" value={issue.assignee ?? ""} onChange={e => void assign(e.target.value)} disabled={closedForGood}>
            <option value="">Unassigned</option>
            {assigneeOptions.map(a => <option key={a.userId} value={a.userId}>{a.name}</option>)}
          </SelectField>
        </div>
        <div>
          <Label>Priority</Label>
          <SelectField size="sm" value={issue.priority} onChange={e => void patch({ priority: e.target.value }, "priority")}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </SelectField>
        </div>
        <div>
          <Label>Module</Label>
          <SelectField size="sm" value={issue.module ?? ""} onChange={e => void patch({ module: e.target.value || null }, "module")}>
            <option value="">— none —</option>
            {issue.module && !MODULE_LABELS[issue.module] && <option value={issue.module}>{issue.module}</option>}
            {Object.entries(MODULE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </SelectField>
        </div>
      </div>

      {!closedForGood && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px] flex-1">
              <Label>Change status (now {issueStatusLabel(issue.status)})</Label>
              <SelectField size="sm" value={target} onChange={e => { setTarget(e.target.value); setErr(null); }}>
                <option value="">Choose…</option>
                {choices.map(a => <option key={a.status} value={a.status}>{a.label}</option>)}
              </SelectField>
            </div>
            <AsyncButton size="sm" variant={action?.needsReason ? "warning" : "default"} onClick={() => applyStatus()} disabled={!target}>
              Apply
            </AsyncButton>
          </div>
          {action && <p className="text-xs text-surface-500">{action.hint}</p>}
          {target === "duplicate" && (
            <Input
              value={duplicateOf}
              onChange={e => setDuplicateOf(e.target.value)}
              placeholder="Original issue, e.g. HRMS-0042"
              aria-label="Original issue ID"
            />
          )}
          {target === "deferred" && (
            <div>
              <Label>Revisit on (optional)</Label>
              <DateInput value={deferredUntil} onChange={setDeferredUntil} />
            </div>
          )}
          {action && (
            <Textarea
              size="sm"
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={action.needsReason ? "Reason (required) — the reporter sees this" : "Note (optional)"}
            />
          )}
        </div>
      )}

      {issue.statusReason && (
        <p className="text-xs text-surface-600">
          <span className="font-semibold">{issueStatusLabel(issue.status)} because:</span> {issue.statusReason}
        </p>
      )}
      {err && <p className="text-xs text-error">{err}</p>}
      <IssueLinks issue={issue} onOpenIssue={onOpenIssue} />
    </div>
  );
}

/** Duplicate links and the defer date, shown to everyone. */
export function IssueLinks({ issue, onOpenIssue }: { issue: TriageIssue; onOpenIssue: (id: string) => void }) {
  const dupes = issue.duplicates ?? [];
  if (!issue.duplicateOf && dupes.length === 0 && !issue.deferredUntil) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-surface-600">
      {issue.deferredUntil && <Badge tone="outline">Deferred until {new Date(issue.deferredUntil).toLocaleDateString()}</Badge>}
      {issue.duplicateOf && (
        <button type="button" onClick={() => onOpenIssue(issue.duplicateOf!.id)} className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline">
          <Link2 size={12} /> Duplicate of {fmtId(issue.duplicateOf.displayId)} · {issue.duplicateOf.title}
        </button>
      )}
      {dupes.map(d => (
        <button key={d.id} type="button" onClick={() => onOpenIssue(d.id)} className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline">
          <Link2 size={12} /> {fmtId(d.displayId)} is a duplicate of this
        </button>
      ))}
    </div>
  );
}

/** Page, browser and console errors the report panel captured. */
export function IssueAutoCaptured({ issue }: { issue: TriageIssue }) {
  const [open, setOpen] = useState(false);
  if (!issue.pageUrl && !issue.browser) return null;
  const errors = issue.consoleErrors ?? [];
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-50 p-3 text-xs">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="flex w-full items-center gap-1.5 text-left font-semibold text-surface-700">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Info size={14} /> Auto-captured details
        <span className="ml-auto flex gap-1">
          {issue.browser && <Badge tone="outline">{issue.browser}</Badge>}
          {errors.length > 0 && <Badge tone="error">{errors.length} console error{errors.length === 1 ? "" : "s"}</Badge>}
        </span>
      </button>
      {open && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-surface-600">
          {issue.pageUrl && (<><dt className="font-medium">Page</dt><dd className="break-all"><a href={issue.pageUrl} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">{issue.pageUrl}</a></dd></>)}
          <dt className="font-medium">Browser</dt><dd>{[issue.browser, issue.os].filter(Boolean).join(" on ") || "—"}</dd>
          {issue.viewport && (<><dt className="font-medium">Screen</dt><dd>{issue.viewport}</dd></>)}
          {issue.appVersion && (<><dt className="font-medium">Build</dt><dd>{issue.appVersion}</dd></>)}
          {issue.userAgent && (<><dt className="font-medium">User agent</dt><dd className="break-all">{issue.userAgent}</dd></>)}
          <dt className="font-medium">Errors</dt>
          <dd>
            {errors.length === 0 ? "None recorded" : (
              <ul className="max-h-40 list-none space-y-0.5 overflow-auto font-mono text-[11px]">
                {errors.map((e, i) => <li key={i} className="break-all">{e}</li>)}
              </ul>
            )}
          </dd>
        </dl>
      )}
    </div>
  );
}
